import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection, EASE } from "@/lib/scroll";

const LINKS = [
  { label: "Manifesto", href: "#manifesto", testid: "nav-link-manifesto" },
  { label: "Gallery", href: "#gallery", testid: "nav-link-gallery" },
  { label: "Amenities", href: "#amenities", testid: "nav-link-amenities" },
  { label: "Rates", href: "#rates", testid: "nav-link-rates" },
  { label: "Local Life", href: "#scene", testid: "nav-link-scene" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-[#1E3323]/10 bg-[#F7F4EF]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("#hero")}
          className="flex items-baseline gap-2 text-left"
        >
          <span
            className={`font-serif text-xl font-semibold tracking-tight transition-colors duration-500 ${
              scrolled ? "text-[#1E3323]" : "text-[#F7F4EF]"
            }`}
          >
            Victoria Beach Cottage . ca
          </span>
          <span
            className={`hidden font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-500 sm:inline ${
              scrolled ? "text-[#C8634B]" : "text-[#F7F4EF]/70"
            }`}
          >
            Est. 1922
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links-desktop">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={link.testid}
              onClick={() => go(link.href)}
              className={`group relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-500 ${
                scrolled ? "text-[#4A4D46] hover:text-[#1E3323]" : "text-[#F7F4EF]/80 hover:text-[#F7F4EF]"
              }`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C8634B] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            data-testid="nav-enquire-button"
            onClick={() => go("#enquire")}
            className={`rounded-full px-6 py-2.5 font-sans text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
              scrolled
                ? "bg-[#1E3323] text-[#F7F4EF] hover:bg-[#C8634B]"
                : "border border-[#F7F4EF]/40 bg-[#F7F4EF]/10 text-[#F7F4EF] backdrop-blur-md hover:bg-[#C8634B] hover:border-[#C8634B]"
            }`}
          >
            Enquire for 2027
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className={`p-2 transition-colors duration-500 lg:hidden ${
            scrolled ? "text-[#1E3323]" : "text-[#F7F4EF]"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-links-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-b border-[#1E3323]/10 bg-[#F7F4EF]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`${link.testid}-mobile`}
                  onClick={() => go(link.href)}
                  className="py-3 text-left font-serif text-2xl text-[#1E3323]"
                >
                  {link.label}
                </button>
              ))}
              <button
                data-testid="nav-enquire-button-mobile"
                onClick={() => go("#enquire")}
                className="mt-4 rounded-full bg-[#C8634B] px-6 py-3.5 text-center font-sans text-sm font-semibold uppercase tracking-[0.15em] text-[#F7F4EF]"
              >
                Enquire for 2027
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
